// react
import { useCallback, useMemo } from 'react';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

/**
 * useUrlSeu
 *
 * Stores the selected SEU (string) in the URL query string for deep linking.
 *
 * Default param name: "seu" -> ?seu=<value>
 *
 * Usage:
 *   const [seu, setSeu] = useUrlSeu({ allowed: validSeuOptions, fallback: () => validSeuOptions[0] ?? null });
 *
 * Options:
 * - key: query param name (default "seu")
 * - allowed: array of allowed seu values (optional)
 * - fallback: string | () => string | null (optional)
 * - replace: boolean (default true) -> use history replace to avoid spamming back button
 */
export default function useUrlSeu(options = {}) {
  const { key = 'type', allowed, fallback, replace = true } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  const rawValue = searchParams.get(key);

  const normalized = useMemo(() => {
    // Treat empty string as "unset"
    if (!rawValue) return null;

    // If an allow-list is provided, enforce it
    if (Array.isArray(allowed) && allowed.length > 0) {
      return allowed.includes(rawValue) ? rawValue : null;
    }

    return rawValue;
  }, [rawValue, allowed]);

  const resolvedFallback = useMemo(() => {
    if (typeof fallback === 'function') return fallback();
    return fallback ?? null;
  }, [fallback]);

  // The value the component should use
  const value = normalized ?? resolvedFallback ?? null;

  const setSeu = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);

          const nextValue =
            next === null || next === undefined || next === ''
              ? null
              : String(next);

          if (!nextValue) {
            sp.delete(key);
          } else {
            sp.set(key, nextValue);
          }

          return sp;
        },
        { replace },
      );
    },
    [key, replace, setSearchParams],
  );

  return [value, setSeu];
}
